/* eslint-disable no-param-reassign */

'use strict';

// matches all non-numeric characters. (Note: a previous change added "e" to the list of
// characters, to allow strings in scientific notation format, but this was rolled back
// because it caused too many issues.)
var nonNumericCharsRegex = /[^0-9.+-]+/g;

// matches properly-formatted numbers. This allows scientific notation, but above regex
// will strip out 'e' character so no scientific notation numbers will make it to this
// regex. But I'm leaving the regex unchanged in case we want to try to support scientific
// notation for strings in the future.
var numberRegex = /^[+-]?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/;
var commonFormatHelper = {
  addCommas: function addCommas(num, format) {
    if (format.show_commas && typeof format.mark_thousands === 'undefined' || format.mark_thousands && format.mark_thousands !== 'none') {
      if (!format.mark_thousands) {
        format.mark_thousands = 'comma';
      }
      var decimalPlace = '.';
      var markThousands = format.mark_thousands;
      switch (markThousands) {
        case 'comma':
          markThousands = ',';
          break;
        case 'space':
          markThousands = ' ';
          break;
        case 'period':
          markThousands = '.';
          break;
        default:
          markThousands = ',';
          break;
      }
      num = String(num);
      var x = num.split(decimalPlace);
      var x1 = x[0];
      var x2 = x.length > 1 ? decimalPlace + x[1] : '';
      var rgx = /(\d+)(\d{3})/;
      while (rgx.test(x1)) {
        x1 = x1.replace(rgx, "$1".concat(markThousands, "$2"));
      }
      return x1 + x2;
    }
    return num;
  },
  addPrefixOrPostfix: function addPrefixOrPostfix(num, format) {
    if (format.format && format.format.toLowerCase() !== 'none') {
      switch (format.format) {
        case 'custom':
          format.pre = format.pre || '';
          format.post = format.post || '';
          num = String(format.pre) + num + String(format.post);
          break;
        case '€_after':
          num += '&nbsp;€';
          break;
        case '%':
          num += '%';
          break;
        default:
          num = String(format.format) + num;

          // check if negative number (can't do < 0 because num is a string now)
          if (String(num).indexOf('-') > -1) {
            num = num.replace("".concat(format.format, "-"), "-".concat(format.format));
          }
          break;
      }
    } else if (format.prefix && format.prefix.toLowerCase() !== 'none') {
      num = String(format.prefix) + num;
    }
    return num;
  },
  markDecimals: function markDecimals(num, format) {
    // decimal comma format
    // eslint-disable-next-line no-restricted-globals
    if (format.mark_decimal && format.mark_decimal === 'comma' && format.precision && !isNaN(format.precision) && Number(format.precision) > 0) {
      var parts = String(num).split('.');

      // if the parts length is 1, that means we will display the whole number as a decimal
      if (parts.length > 1) {
        var decimal = parts.pop();
        num = "".concat(parts.join('.'), ",").concat(decimal);
      }
    }
    return num;
  },
  roundNumber: function roundNumber(num, format) {
    // clamp precision to the range [0..10]. Capping at 10 because we don't allow users to select
    // precision greater than 10 anywhere in the UI, and we only store to 10 digits in the database
    var precision = format.mark_decimal === 'none' ? 0 : Math.min(10, Math.max(0, Number(format.precision) || 0));

    // String(n) will use scientific notation for any number smaller than 0.000001 (1e-6). To avoid that,
    // use toFixed(15) which will not use scientific notation. Using 15 because that is the largest number
    // of significant digits that IEEE floats can reliably represent. For example,
    // (0.1).toFixed(17) => '0.10000000000000001'.
    // This does leave an edge case where a value smaller than 1e-15 is treated as being equal to zero.
    // Since we don't allow numbers that small anywhere, such a number is almost certainly the result
    // of another floating point math roundoff error.
    var numToStringWithoutExponent = function numToStringWithoutExponent(n) {
      return Math.abs(n) < 1e-6 ? n.toFixed(15) : String(n);
    };
    var numString = numToStringWithoutExponent(num);
    if (numString.includes('e')) {
      // this is a really large number and the "safe" rounding below won't work. Just use the old logic.
      // Round up/down doesn't actually matter in this case because this number is so big that the digits
      // after the decimal place are noise anyway.
      var precisionDecimals = Math.pow(10, precision);
      num = Math.round(num * precisionDecimals) / precisionDecimals;
    } else {
      // to avoid floating point math oddities like 1147.1 * 100 = 114709.99999999999, i'm using
      // a version of the logic from here: http://www.jacklmoore.com/notes/rounding-in-javascript/
      num = Number("".concat(numString, "e").concat(precision)); // multiply by 10^precision but as a string to prevent roundoff errors
      if (format.rounding === 'up') {
        num = Math.ceil(num);
      } else if (format.rounding === 'down') {
        num = Math.floor(num);
      } else {
        num = Math.round(num);
      }
      numString = numToStringWithoutExponent(num);
      num = Number("".concat(num, "e-").concat(precision)); // divide by 10^precision but as a string to prevent roundoff errors
    }

    // with these options we need to return a string
    if (format.precision > 0 && (!format.mark_decimal || format.mark_decimal !== 'none') || format.format === '%') {
      num = num.toFixed(precision);
      if (num === '-0') {
        num = '0';
      }
    }
    return num;
  },
  formatNumber: function formatNumber(val, format) {
    var num = val;

    // Note: I'm including this case out of extreme caution to avoid potentially breaking old code.
    // The old code would implicitly convert the array to a number, and a single-value array acts
    // like the value of the one element, but multi-value array is NaN.
    // For example, Number([1.23]) ==> 1.23,  Number([1,2,3]) ==> NaN
    if (Array.isArray(num) && num.length === 1) {
      // eslint-disable-next-line prefer-destructuring
      num = num[0];
    }
    if (typeof num === 'string') {
      // strip out non-numeric characters
      num = num.replace(nonNumericCharsRegex, '');
      if (!numberRegex.test(num)) {
        return '';
      }
      num = Number(num);
    }
    if (!Number.isFinite(num)) {
      return '';
    }
    if (!format) {
      format = {};
    }
    if (format.format && format.format === '%') {
      num *= 100;
    }
    num = this.roundNumber(num, format);
    num = this.addCommas(num, format);
    num = this.addPrefixOrPostfix(num, format);
    num = this.markDecimals(num, format);
    return num;
  },
  formatCurrency: function formatCurrency(val, format) {
    var currencyFormat = {
      precision: 2,
      show_commas: 1,
      format: format && format.format ? format.format : '$'
    };
    return this.formatNumber(val, currencyFormat);
  }
};
module.exports = commonFormatHelper;